import { type Notification, notify } from '@pique/component/ui/notification';
import type { ExternalToast } from 'sonner';
import type { HTMLAttributes, ReactNode } from 'react';

import {
  commands as notificationCommands,
  type NotificationAction,
} from '../../tauri/plugins/notification';

export const MEETING_ACTION_LABEL = 'Start Transcribing';
export const GENERATE_NOTES_ACTION_LABEL = 'Generate Notes';
export const PRE_READ_ACTION_LABEL = 'Prepare Me';

export interface EnhancedNotificationOptions extends Notification {
  style?: 'normal' | 'information' | 'alert';
  theme?: 'info' | 'success' | 'warning' | 'error';
  borderColor?: string;
  background?: string;
  foreground?: string;
  alignMessage?: 'title' | 'icon';
  title?: ReactNode;
  message?: ReactNode;
  icon?: ReactNode;
  iconColor?: string;
  thumb?: ReactNode;
  footer?: ReactNode;
  action?: {
    label: ReactNode;
    onClick: (() => void) | (() => Promise<void>);
    buttonProps?: Record<string, unknown>;
    autoClose?: boolean;
  };
  onDismiss?: () => void;
  rootAttrs?: HTMLAttributes<HTMLDivElement>;
  url?: string;
  timeout?: number;
  subtitle?: string;
  actionLabel?: string;
  sound?: boolean;
  threadId?: string;
  interruptionLevel?: 'passive' | 'active' | 'timeSensitive' | 'critical';
  relevanceScore?: number;
  actionOnBodyClick?: boolean;
  app_name?: string;
  urgency?: 'Low' | 'Normal' | 'Critical';
  category?: string;
  actions?: NotificationAction[];
  resident?: boolean;
  sound_name?: string;
  image_path?: string;
  custom_hints?: Record<string, string>;
  tauriOnly?: boolean;
}

export async function showNotificationPermissionPrompt(options?: {
  title?: string;
  message?: string;
}): Promise<string | number> {
  return notify(
    {
      title: options?.title || 'Notifications Disabled',
      message:
        options?.message ||
        'Please enable notifications for Pique in System Settings to receive alerts.',
      theme: 'warning',
      action: {
        label: 'Open Settings',
        onClick: async () => {
          try {
            await notificationCommands.openNotificationSettings();
          } catch (e) {
            console.warn(
              '[showNotificationPermissionPrompt] Failed to open notification settings:',
              e
            );
          }
        },
        autoClose: true,
      },
    },
    { duration: Infinity }
  );
}

export async function enhancedNotify(
  notification: EnhancedNotificationOptions,
  options?: ExternalToast
): Promise<string | number> {
  const notificationConfig = notification;

  if (environment.isTauri) {
    try {
      await notificationCommands.showCustomNotification({
        title: String(notificationConfig.title || 'Notification'),
        message: String(notificationConfig.message || ''),
        url: notificationConfig.url,
        timeout: notificationConfig.timeout,
        actionLabel: notificationConfig.actionLabel,
        subtitle: notificationConfig.subtitle,
        sound: notificationConfig.sound,
        threadId: notificationConfig.threadId,
        interruptionLevel: notificationConfig.interruptionLevel,
        relevanceScore: notificationConfig.relevanceScore,
        actionOnBodyClick: notificationConfig.actionOnBodyClick,
      });

      return 'tauri-notification-' + Date.now();
    } catch (error) {
      const errorStr = error instanceof Error ? error.message : String(error);
      const isPermissionDenied = errorStr.includes(
        'NotGrantedAndShouldAskManual'
      );

      if (isPermissionDenied) {
        void showNotificationPermissionPrompt();
      }

      if (notificationConfig.tauriOnly) {
        return 'tauri-notification-failed-' + Date.now();
      }
      return notify(notificationConfig, options);
    }
  }
  if (notificationConfig.tauriOnly) {
    return 'skipped-not-tauri-' + Date.now();
  }
  return notify(notificationConfig, options);
}

// Enhanced versions of the notify methods
enhancedNotify.success = async (
  notification: EnhancedNotificationOptions,
  options?: ExternalToast
) => {
  return enhancedNotify(
    {
      icon: notification.icon,
      style: 'normal',
      theme: 'success',
      ...notification,
    },
    options
  );
};

enhancedNotify.error = async (
  notification: EnhancedNotificationOptions,
  options?: ExternalToast
) => {
  return enhancedNotify(
    {
      icon: notification.icon,
      style: 'normal',
      theme: 'error',
      ...notification,
    },
    options
  );
};

enhancedNotify.warning = async (
  notification: EnhancedNotificationOptions,
  options?: ExternalToast
) => {
  return enhancedNotify(
    {
      icon: notification.icon,
      style: 'normal',
      theme: 'warning',
      ...notification,
    },
    options
  );
};

enhancedNotify.dismiss = (id: string | number) => {
  // Tauri or web fallback: delegate to in-app; native OS close not supported here
  notify.dismiss(id);
};
